import { useCallback, useEffect, useRef, useState } from "react";
import localStorageService from "../services/localStorageService";
/**
 * Thin reactive wrapper around `localStorageService` for a single string value.
 * Reads the persisted value once on mount and exposes a setter that writes through.
 *
 * Contract:
 * - `value` is `undefined` until the async read settles, then reflects storage.
 * - `setValue(next)` updates local state synchronously and persists through
 *   `localStorageService.setItem`. Empty strings are not persisted (treated as unset).
 * - Validation / fallback / policy is the caller's concern — this hook is intentionally
 *   policy-free so it can be reused for any string-valued preference.
 */
export const useValueFromStorage = ({ key, }) => {
    const [stateValue, setStateValue] = useState(undefined);
    // Monotonic token invalidating any in-flight async read when the effect is
    // superseded (unmount, key change) OR when the caller performs an explicit
    // `update` — without this second case, a late-resolving `getItem` would
    // clobber the user's just-written value.
    const refLoadToken = useRef(0);
    useEffect(() => {
        setStateValue(undefined);
        refLoadToken.current += 1;
        const myToken = refLoadToken.current;
        // `localStorageService.getItem` transparently returns whatever the
        // injected `localAsyncStorage` returns: a Promise on native
        // (AsyncStorage), or a raw `string | null` on web (`window.localStorage`).
        // Normalize with `Promise.resolve` so `.then` is always safe.
        void Promise.resolve(localStorageService.getItem(key)).then((persisted) => {
            if (refLoadToken.current !== myToken) {
                return;
            }
            setStateValue(persisted ?? undefined);
        });
        return () => {
            refLoadToken.current += 1;
        };
    }, [key]);
    const update = useCallback((next) => {
        refLoadToken.current += 1;
        setStateValue(next);
        if (next) {
            void localStorageService.setItem(key, next);
        }
    }, [key]);
    return [stateValue, update];
};
